import { useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { Outlet, useLocation } from 'react-router';
import { configureAppToasts } from '../notifications/toast';
import { PlatformHeader, type PlatformHeaderProps } from './Header';
import { ShellOutletErrorBoundary } from './ShellOutletErrorBoundary';
import { ShellToolbarProvider } from './ShellToolbarContext';
import { PlatformSideNav } from './SideNav';
import type { NavLeaf } from './types';
import type { StoreApi, UseBoundStore } from 'zustand';
import type { ShellPreferencesStoreState } from './createShellPreferencesStore';
import { readShellLayoutState, SHELL_COMPACT_NAV_BREAKPOINT, useShellLayout } from './useShellLayout';

const AUTO_HIDE_HOVER_STRIP_PX = 10;

const DEFAULT_SIDEBAR_WIDTH = 300;
const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 600;
const DRAWER_MAX_SIZE = 280;

function clampWidth(n: number): number {
  return Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, n));
}

function readPersistedSidebarWidth(storageKey: string): number {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return DEFAULT_SIDEBAR_WIDTH;
    const num = Number.parseInt(raw, 10);
    if (!Number.isFinite(num)) return DEFAULT_SIDEBAR_WIDTH;
    return clampWidth(num);
  } catch {
    return DEFAULT_SIDEBAR_WIDTH;
  }
}

function initialDesktopNavOpen(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return !window.matchMedia(SHELL_COMPACT_NAV_BREAKPOINT).matches;
}

export interface PlatformAppShellProps {
  storageKeyPrefix: string;
  header: Omit<PlatformHeaderProps, 'navOpen' | 'onNavToggle' | 'useCompactNav' | 'isTouchPrimary' | 'isNarrow'>;
  navItems: NavLeaf[];
  useShellPreferencesStore?: UseBoundStore<StoreApi<ShellPreferencesStoreState>>;
}

function resolveInitialNavOpen(autoHideSideMenu: boolean, useCompactNav: boolean): boolean {
  if (useCompactNav) return false;
  if (autoHideSideMenu) return false;
  return initialDesktopNavOpen();
}

export function PlatformAppShell({
  storageKeyPrefix,
  header,
  navItems,
  useShellPreferencesStore
}: PlatformAppShellProps) {
  const location = useLocation();
  const { useCompactNav, isTouchPrimary } = useShellLayout();
  const autoHideSideMenu = useShellPreferencesStore?.((s) => s.autoHideSideMenu) ?? false;
  const showNavMenuSearch = useShellPreferencesStore?.((s) => s.showNavMenuSearch) ?? false;
  const showNavExpandAll = useShellPreferencesStore?.((s) => s.showNavExpandAll) ?? false;
  const showNavCollapseAll = useShellPreferencesStore?.((s) => s.showNavCollapseAll) ?? false;
  const sidebarWidthStorageKey = `${storageKeyPrefix}:sidebar-width`;
  const navStateCookieName = `${storageKeyPrefix}:nav-state`;
  const [navOpen, setNavOpen] = useState(() =>
    typeof window === 'undefined'
      ? true
      : resolveInitialNavOpen(autoHideSideMenu, readShellLayoutState().useCompactNav)
  );
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => readPersistedSidebarWidth(sidebarWidthStorageKey));
  const isDraggingRef = useRef(false);
  const latestWidthRef = useRef(sidebarWidth);
  const wasCompactNavRef = useRef(useCompactNav);
  const desktopNavWasOpenRef = useRef(!autoHideSideMenu);

  useEffect(() => {
    latestWidthRef.current = sidebarWidth;
  }, [sidebarWidth]);

  useEffect(() => {
    configureAppToasts();
  }, []);

  useEffect(() => {
    if (useCompactNav && !wasCompactNavRef.current) {
      desktopNavWasOpenRef.current = navOpen;
      setNavOpen(false);
    } else if (!useCompactNav && wasCompactNavRef.current) {
      const restoreOpen = autoHideSideMenu ? false : desktopNavWasOpenRef.current;
      setNavOpen(restoreOpen);
    } else if (!useCompactNav) {
      desktopNavWasOpenRef.current = navOpen;
    }
    wasCompactNavRef.current = useCompactNav;
  }, [autoHideSideMenu, navOpen, useCompactNav]);

  useEffect(() => {
    if (useCompactNav) return;
    if (autoHideSideMenu) setNavOpen(false);
  }, [autoHideSideMenu, useCompactNav]);

  useEffect(() => {
    if (!useCompactNav || !navOpen) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setNavOpen(false);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navOpen, useCompactNav]);

  useEffect(() => {
    if (!useCompactNav || !navOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [navOpen, useCompactNav]);

  useEffect(() => {
    if (useCompactNav) return;
    function onMouseMove(e: MouseEvent) {
      if (!isDraggingRef.current) return;
      setSidebarWidth(clampWidth(e.clientX));
    }
    function onMouseUp() {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.classList.remove('is-resizing-sidebar');
      try {
        localStorage.setItem(sidebarWidthStorageKey, String(latestWidthRef.current));
      } catch {
        /* localStorage may be disabled */
      }
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [useCompactNav, sidebarWidthStorageKey]);

  function startResize(e: ReactMouseEvent) {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.classList.add('is-resizing-sidebar');
  }

  function resetWidth() {
    setSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
    try {
      localStorage.setItem(sidebarWidthStorageKey, String(DEFAULT_SIDEBAR_WIDTH));
    } catch {
      /* ignore */
    }
  }

  function closeNavOnNavigate() {
    if (useCompactNav) setNavOpen(false);
  }

  const sideNav = (
    <PlatformSideNav
      navStateCookieName={navStateCookieName}
      items={navItems}
      onNavigate={closeNavOnNavigate}
      showMenuSearch={showNavMenuSearch}
      showExpandAll={showNavExpandAll}
      showCollapseAll={showNavCollapseAll}
    />
  );

  const mainContent = (
    <main className="app-shell-main">
      {/* key resets the boundary when the route changes after a crash */}
      <ShellOutletErrorBoundary key={location.pathname}>
        <Outlet />
      </ShellOutletErrorBoundary>
    </main>
  );

  const desktopAutoHide = autoHideSideMenu && !useCompactNav;

  function openAutoHideNav() {
    if (desktopAutoHide) setNavOpen(true);
  }

  function closeAutoHideNav() {
    if (desktopAutoHide) setNavOpen(false);
  }

  return (
    <ShellToolbarProvider>
      <div
        className={[
          'app-shell',
          useCompactNav ? 'app-shell--compact-nav' : '',
          useCompactNav && navOpen ? 'app-shell--nav-open' : '',
          isTouchPrimary ? 'app-shell--touch-primary' : '',
          desktopAutoHide ? 'app-shell--auto-hide-nav' : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <PlatformHeader
          navOpen={navOpen}
          useCompactNav={useCompactNav}
          isTouchPrimary={isTouchPrimary}
          onNavToggle={() => setNavOpen((open) => !open)}
          {...header}
        />
      {useCompactNav ? (
        <div className="app-shell-body app-shell-body--compact">
          {navOpen ? (
            <button
              type="button"
              className="app-shell-mobile-nav-backdrop"
              aria-label="Close menu"
              onClick={() => setNavOpen(false)}
            />
          ) : null}
          <aside
            id="app-sidebar"
            className={['app-shell-mobile-nav', navOpen ? 'app-shell-mobile-nav--open' : '']
              .filter(Boolean)
              .join(' ')}
            aria-hidden={!navOpen}
            style={{ width: `min(${DRAWER_MAX_SIZE}px, 85vw)` }}
          >
            {sideNav}
          </aside>
          {mainContent}
        </div>
      ) : (
        <div className="app-shell-body">
          {desktopAutoHide && !navOpen ? (
            <div
              className="app-shell-hover-strip"
              style={{ width: AUTO_HIDE_HOVER_STRIP_PX }}
              aria-hidden
              onMouseEnter={openAutoHideNav}
            />
          ) : null}
          <div
            className="app-shell-sidebar-zone"
            onMouseLeave={closeAutoHideNav}
          >
            <aside
              id="app-sidebar"
              className={navOpen ? 'app-shell-sidebar' : 'app-shell-sidebar app-shell-sidebar-collapsed'}
              style={navOpen ? { flex: `0 0 ${sidebarWidth}px`, width: sidebarWidth } : undefined}
              aria-hidden={!navOpen}
              onMouseEnter={openAutoHideNav}
            >
              {sideNav}
            </aside>
            {navOpen ? (
              <div
                className="app-shell-resizer"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize sidebar"
                onMouseDown={startResize}
                onDoubleClick={resetWidth}
                title="Drag to resize, double-click to reset"
              />
            ) : null}
          </div>
          {mainContent}
        </div>
      )}
      </div>
    </ShellToolbarProvider>
  );
}
